import React, { useLayoutEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { LuCheck, LuPlus, LuX } from 'react-icons/lu';
import { useNodeForm } from '../Components/NodeFormKit';

export const AGENT_FIELDS_CSS = `
.su-af-section { padding: 16px 0; border-bottom: 1px solid var(--su-line-2); }
.su-af-section:first-child { padding-top: 0; }
.su-af-section:last-child { border-bottom: none; padding-bottom: 0; }
.su-af-section > h4 { margin: 0 0 12px; font-family: inherit; font-size: 11.5px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--su-ink-3); }
.su-af-field { margin-bottom: 14px; min-width: 0; }
.su-af-field:last-child { margin-bottom: 0; }
.su-af-label { display: block; margin: 0 0 5px; font-size: 12px; font-weight: 600; color: var(--su-ink-3); }
.su-af-hint { margin-top: 4px; font-size: 12px; line-height: 1.4; color: var(--su-ink-3); }
.su-kit .su-af-in { display: block; width: 100%; padding: 9px 11px; border: 1px solid var(--su-line) !important; border-radius: 10px; outline: none; box-shadow: none !important; background: var(--su-surface) !important; color: var(--su-ink) !important; font-family: inherit; font-size: 14px; font-weight: 400; line-height: 1.45; resize: none; overflow: hidden; }
.su-kit .su-af-in:focus { border-color: var(--su-sky) !important; }
.su-kit .su-af-in::placeholder { color: var(--su-ink-3); font-style: normal; }
.su-kit .su-af-in.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12.5px; }
.su-af-item { display: flex; align-items: flex-start; gap: 8px; padding: 2px 4px 2px 10px; border: 1px solid var(--su-line); border-radius: 10px; background: var(--su-surface); }
.su-af-item:focus-within { border-color: var(--su-sky); }
.su-af-item > .su-ic { margin-top: 10px; }
.su-kit .su-af-item textarea { flex: 1; min-width: 0; padding: 7px 0; border: none !important; outline: none; box-shadow: none !important; background: transparent !important; color: var(--su-ink) !important; font-family: inherit; font-size: 14px; line-height: 1.45; resize: none; overflow: hidden; }
.su-kit .su-af-item textarea::placeholder { color: var(--su-ink-3); font-style: normal; }
.su-af-list { display: flex; flex-direction: column; gap: 6px; }
.su-af-x { width: 30px; height: 30px; flex-shrink: 0; display: grid; place-items: center; border: none; border-radius: 8px; background: none; color: var(--su-ink-3); cursor: pointer; }
.su-af-x:hover { background: var(--su-raised); color: var(--su-red-ink); }
.su-af-add { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; padding: 8px; border: 1px dashed var(--su-control); border-radius: 10px; background: none; color: var(--su-ink-3); font-size: 12.5px; font-weight: 600; cursor: pointer; }
.su-af-add:hover { border-color: var(--su-accent-ink); color: var(--su-accent-ink); }
.su-af-toggle { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 4px 0; cursor: pointer; }
.su-af-toggle b { display: block; font-size: 14.5px; font-weight: 400; color: var(--su-ink); }
.su-af-toggle small { display: block; margin-top: 1px; font-size: 12px; line-height: 1.4; color: var(--su-ink-3); }
.su-af-inline { display: inline-flex; align-items: center; gap: 8px; padding: 4px 0; border: none; background: none; color: var(--su-ink-2); font-size: 13px; font-weight: 600; white-space: nowrap; cursor: pointer; }
.su-af-chip { display: inline-flex; align-items: center; gap: 5px; padding: 5px 11px; border: 1px solid var(--su-line); border-radius: 99px; background: var(--su-surface); color: var(--su-ink-2); font-size: 12.5px; font-weight: 600; text-align: left; cursor: pointer; }
.su-af-chip:hover { border-color: var(--su-control); }
.su-af-chip.on { border-color: var(--su-accent-ink); background: var(--su-chip-on); color: var(--su-accent-ink); }
`;

const useAutoHeight = (value) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = 'auto';
    ref.current.style.height = `${ref.current.scrollHeight + 2}px`;
  }, [value]);
  return ref;
};

export const FormSection = ({ title, children, style }) => (
  <div className="su-af-section" style={style ? { ...style, marginBottom: undefined } : undefined}>
    {title && <h4>{title}</h4>}
    {children}
  </div>
);

export const Field = ({ label, hint, children, style }) => (
  <div className="su-af-field" style={style}>
    {label && <label className="su-af-label">{label}</label>}
    {children}
    {hint && <div className="su-af-hint">{hint}</div>}
  </div>
);

export const ToggleField = ({ label, hint, checked, onChange, disabled }) => (
  <div className="su-af-toggle" style={disabled ? { opacity: 0.5, cursor: 'default' } : undefined} onClick={() => !disabled && onChange(!checked)}>
    <div>
      <b>{label}</b>
      {hint && <small>{hint}</small>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(checked)}
      aria-label={typeof label === 'string' ? label : undefined}
      className={`su-sw${checked ? ' on' : ''}`}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
    >
      <span />
    </button>
  </div>
);

export const TextInput = ({ value, onChange, placeholder, mono, ...rest }) => (
  <input
    type="text"
    className={`su-af-in${mono ? ' mono' : ''}`}
    value={value ?? ''}
    placeholder={placeholder}
    onChange={(event) => onChange(event.target.value)}
    {...rest}
  />
);

export const TextArea = ({ value, onChange, placeholder, rows = 3 }) => {
  const ref = useAutoHeight(value);
  return (
    <textarea
      ref={ref}
      className="su-af-in"
      rows={rows}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

const selectStyles = (t) => ({
  option: (provided, state) => ({
    ...provided,
    borderRadius: '8px',
    backgroundColor: state.isSelected ? t.selected || t.inputBg : state.isFocused ? t.inputBg : 'transparent',
    color: state.isSelected ? t.accent : t.textPrimary,
    fontSize: '14px',
    cursor: 'pointer',
    '&:active': { backgroundColor: t.inputBg },
  }),
  control: (provided, state) => ({
    ...provided,
    backgroundColor: t.cardBg,
    border: `1px solid ${state.isFocused ? '#5DCCED' : t.border}`,
    borderRadius: '10px',
    boxShadow: 'none',
    minHeight: '40px',
    fontSize: '14px',
    cursor: 'pointer',
    '&:hover': { borderColor: state.isFocused ? '#5DCCED' : t.borderStrong || t.border },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: t.panelBg,
    border: `1px solid ${t.border}`,
    borderRadius: '12px',
    boxShadow: '0 14px 36px -12px rgba(0, 0, 0, 0.35)',
    overflow: 'hidden',
  }),
  menuList: (provided) => ({ ...provided, padding: '6px' }),
  menuPortal: (provided) => ({ ...provided, zIndex: 1400 }),
  singleValue: (provided) => ({ ...provided, color: t.textPrimary }),
  input: (provided) => ({ ...provided, color: t.textPrimary }),
  placeholder: (provided) => ({ ...provided, color: t.textTertiary }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided) => ({ ...provided, color: t.textSecondary, padding: '6px' }),
  clearIndicator: (provided) => ({ ...provided, color: t.textSecondary, padding: '6px' }),
  noOptionsMessage: (provided) => ({ ...provided, color: t.textTertiary, fontSize: '13px' }),
});

export const SelectInput = ({ value, onChange, options, placeholder }) => {
  const { theme } = useNodeForm();
  const styles = useMemo(() => selectStyles(theme), [theme]);
  const selected = options.find((option) => String(option.value) === String(value ?? '')) || null;
  return (
    <Select
      styles={styles}
      classNamePrefix="select"
      value={selected}
      options={options}
      placeholder={placeholder || 'Seleccionar'}
      isClearable={Boolean(placeholder)}
      isSearchable={options.length > 6}
      noOptionsMessage={() => 'Sin opciones'}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPlacement="auto"
      onChange={(option) => onChange(option ? option.value : '')}
    />
  );
};

const MAX_FIELDS = 300;

export const responseFields = (node) => {
  let raw = node?.data?.response_api;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (error) {
      return [];
    }
  }
  if (Array.isArray(raw)) raw = raw[0];
  if (!raw || typeof raw !== 'object') return [];
  const fields = [];
  const walk = (value, path) => {
    if (fields.length >= MAX_FIELDS) return;
    if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        if (value.length) walk(value[0], path ? `${path}.0` : '0');
      } else {
        Object.entries(value).forEach(([key, item]) => walk(item, path ? `${path}.${key}` : key));
      }
      return;
    }
    fields.push({ value: path, label: path, sample: value === null || value === undefined ? '' : String(value).slice(0, 40) });
  };
  walk(raw, '');
  return fields;
};

export const FieldPicker = ({ node, value, onChange, placeholder = 'Elegir campo' }) => {
  const { theme } = useNodeForm();
  const styles = useMemo(() => selectStyles(theme), [theme]);
  const options = useMemo(() => responseFields(node), [node]);
  const current = value ? options.find((option) => option.value === value) || { value, label: value } : null;
  return (
    <CreatableSelect
      styles={styles}
      classNamePrefix="select"
      value={current}
      options={options}
      placeholder={placeholder}
      isClearable
      noOptionsMessage={() => 'Este nodo no tiene respuesta de ejemplo. Escribe el campo.'}
      formatCreateLabel={(text) => `Usar «${text}»`}
      formatOptionLabel={(option, { context }) =>
        context === 'menu' && option.sample ? (
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{option.label}</span>
            <small style={{ color: theme.textTertiary }}>Ej.: {option.sample}</small>
          </span>
        ) : (
          option.label
        )
      }
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPlacement="auto"
      onChange={(option) => onChange(option ? option.value : '')}
    />
  );
};

export const RemoveButton = ({ onClick, title = 'Quitar' }) => (
  <button type="button" className="su-af-x" title={title} aria-label={title} onClick={onClick}>
    <LuX className="su-ic" size={15} />
  </button>
);

export const AddLink = ({ onClick, children }) => (
  <button type="button" className="su-af-add" onClick={onClick}>
    <LuPlus className="su-ic" size={14} />
    {children}
  </button>
);

const ListItem = ({ value, onChange, onRemove, placeholder, icon: Icon, iconColor }) => {
  const ref = useAutoHeight(value);
  return (
    <div className="su-af-item">
      {Icon && <Icon className="su-ic" size={14} color={iconColor || 'var(--su-ink-3)'} />}
      <textarea ref={ref} rows={1} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      <RemoveButton onClick={onRemove} />
    </div>
  );
};

export const StringListEditor = ({ items = [], onChange, placeholder, addLabel = 'Agregar', icon, iconColor }) => {
  const update = (index, value) => onChange(items.map((item, i) => (i === index ? value : item)));
  return (
    <div className="su-af-list">
      {items.map((item, index) => (
        <ListItem
          key={index}
          value={item}
          placeholder={placeholder}
          icon={icon}
          iconColor={iconColor}
          onChange={(value) => update(index, value)}
          onRemove={() => onChange(items.filter((_, i) => i !== index))}
        />
      ))}
      <AddLink onClick={() => onChange([...items, ''])}>{addLabel}</AddLink>
    </div>
  );
};

export const PairListEditor = ({ pairs = [], onChange, keyPlaceholder = 'clave', valuePlaceholder = 'valor', addLabel = 'Agregar' }) => {
  const update = (index, patch) => onChange(pairs.map((pair, i) => (i === index ? { ...pair, ...patch } : pair)));
  return (
    <div className="su-af-list">
      {pairs.map((pair, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '38%' }}>
            <TextInput value={pair.key} onChange={(value) => update(index, { key: value })} placeholder={keyPlaceholder} mono />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <TextInput value={pair.value} onChange={(value) => update(index, { value })} placeholder={valuePlaceholder} mono />
          </div>
          <RemoveButton onClick={() => onChange(pairs.filter((_, i) => i !== index))} />
        </div>
      ))}
      <AddLink onClick={() => onChange([...pairs, { key: '', value: '' }])}>{addLabel}</AddLink>
    </div>
  );
};

export const InlineSwitch = ({ checked, onChange, label, title }) => {
  const on = Boolean(checked);
  return (
    <button type="button" role="switch" aria-checked={on} title={title} className="su-af-inline" onClick={() => onChange(!on)}>
      <span className={`su-sw${on ? ' on' : ''}`}>
        <span />
      </span>
      {label}
    </button>
  );
};

export const SelectChip = ({ selected, onClick, children }) => (
  <button type="button" aria-pressed={selected} className={`su-af-chip${selected ? ' on' : ''}`} onClick={onClick}>
    {selected && <LuCheck className="su-ic" size={13} />}
    {children}
  </button>
);

export const csvToList = (text) =>
  String(text || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
